import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { Loader2 } from "lucide-react";
import type { TourRouteTableEntry } from "@mspca-beti/shared";
import type { TourRecord } from "./types";

interface RoutesResponse {
  routes: TourRouteTableEntry[];
}

interface TourDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audienceId: string;
  audienceLabel: string;
  tour: TourRecord | null;
}

export function TourDialog({ open, onOpenChange, audienceId, audienceLabel, tour }: TourDialogProps) {
  const { toast } = useToast();
  const isEdit = !!tour;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [routePattern, setRoutePattern] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [useCustomRoute, setUseCustomRoute] = useState(false);

  const routesQuery = useQuery<RoutesResponse>({
    queryKey: ["/api/admin/tours/routes"],
    enabled: open,
  });
  const tableRoutes = useMemo(
    () => routesQuery.data?.routes ?? [],
    [routesQuery.data],
  );

  useEffect(() => {
    if (open) {
      setTitle(tour?.title ?? "");
      setDescription(tour?.description ?? "");
      setRoutePattern(tour?.routePattern ?? "");
      setIsActive(tour?.isActive ?? false);
    }
  }, [open, tour]);

  // When the route table loads (or the editing target changes), decide
  // whether to start in picker mode or custom mode. Existing routes that
  // aren't in the scanned table (e.g. hand-authored patterns) drop into
  // custom mode so authors don't lose their value.
  useEffect(() => {
    if (!open || tableRoutes.length === 0) return;
    const existing = tour?.routePattern ?? "";
    const inTable = tableRoutes.some((r) => r.path === existing);
    setUseCustomRoute(existing.length > 0 && !inTable);
  }, [open, tour, tableRoutes]);

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        audienceId,
        title: title.trim(),
        description: description.trim() || null,
        routePattern: routePattern.trim(),
        isActive,
      };
      if (isEdit && tour) {
        return apiRequest("PATCH", `/api/admin/tours/${tour.id}`, body);
      }
      return apiRequest("POST", "/api/admin/tours", body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/tours", audienceId] });
      toast({ title: isEdit ? "Tour updated" : "Tour created" });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const canSubmit = title.length > 0 && routePattern.length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit tour" : "New tour"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Audience</Label>
            <p className="text-sm text-muted-foreground">{audienceLabel}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tour-title">Title</Label>
            <Input
              id="tour-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="input-tour-title"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="tour-route">Route pattern</Label>
              {useCustomRoute ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setUseCustomRoute(false);
                    setRoutePattern("");
                  }}
                  data-testid="button-tour-route-use-picker"
                >
                  Use route picker
                </Button>
              ) : null}
            </div>
            {useCustomRoute ? (
              <Input
                id="tour-route"
                value={routePattern}
                onChange={(e) => setRoutePattern(e.target.value)}
                placeholder="e.g. /admin/* or /custom/route"
                className="font-mono text-sm"
                data-testid="input-tour-route"
              />
            ) : (
              <Combobox<TourRouteTableEntry>
                options={tableRoutes}
                value={routePattern}
                onChange={(key) => setRoutePattern(key)}
                getKey={(r) => r.path}
                getLabel={(r) => `${r.path} — ${r.pageName}`}
                getGroup={(r) => r.group}
                searchKeys={(r) => [r.path, r.pageName, r.group]}
                placeholder={
                  routesQuery.isLoading ? "Loading routes…" : "Pick a page…"
                }
                searchPlaceholder="Search by path or page name…"
                emptyText="No matching routes."
                disabled={routesQuery.isLoading}
                triggerTestId="combobox-tour-route"
                itemTestIdPrefix="combobox-tour-route-item"
                customOption={{
                  label: "Custom… (enter a route by hand)",
                  onSelect: () => {
                    setUseCustomRoute(true);
                    setRoutePattern("");
                  },
                }}
              />
            )}
            <p className="text-xs text-muted-foreground">
              The page path where this tour fires. Static segments
              (<code className="rounded bg-muted px-1">/dashboard</code>),
              params (<code className="rounded bg-muted px-1">/animals/:id</code>),
              and wildcards (<code className="rounded bg-muted px-1">/admin/*</code>)
              are all supported. When multiple tours match the same
              path, the most specific pattern wins.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tour-desc">Description</Label>
            <Textarea
              id="tour-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="input-tour-description"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="tour-active">Active</Label>
            <Switch
              id="tour-active"
              checked={isActive}
              onCheckedChange={setIsActive}
              data-testid="switch-tour-active"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-tour-cancel">
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit} data-testid="button-tour-save">
            {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
